/**
 * WRAP hired-equipment overtime: replacement hour split (not an add-on premium).
 * First 8h @ 1×, hours 8–12 @ 1.5×, hours after 12 @ 2×.
 */

export const HE_OT_STRAIGHT_CAP = 8;
export const HE_OT_15_CAP = 4;
export const HE_OT_15_MULTIPLIER = 1.5;
export const HE_OT_20_MULTIPLIER = 2;

export interface HeOvertimeBuckets {
  straight: number;
  ot15: number;
  ot20: number;
}

export interface HeHourBreakdown {
  regularQty: number;
  ot15Qty: number;
  ot20Qty: number;
  regularCost: number;
  ot15Cost: number;
  ot20Cost: number;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatHours(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/** Case-insensitive Hours / hour / hrs / hr (punctuation ignored). */
export function isHoursUnit(unit: string | null | undefined): boolean {
  const normalized = (unit ?? '').trim().toLowerCase().replace(/[.\s]/g, '');
  return normalized === 'hours' || normalized === 'hour' || normalized === 'hrs' || normalized === 'hr';
}

export function parseOvertimeEligibleFlag(raw: string | null | undefined): boolean {
  const value = (raw ?? '').trim().toLowerCase();
  return value === 'true' || value === 'yes' || value === '1';
}

export function defaultOvertimeEligible(
  resourceOvertimeEligible: boolean | null | undefined,
  unit: string | null | undefined,
): boolean {
  return resourceOvertimeEligible === true && isHoursUnit(unit);
}

export function canToggleHeOvertime(
  resourceOvertimeEligible: boolean | null | undefined,
  unit: string | null | undefined,
): boolean {
  if (!isHoursUnit(unit)) return false;
  if (resourceOvertimeEligible === true) return true;
  // Rehydrated WRAP lines may not have the catalogue flag.
  return resourceOvertimeEligible == null;
}

export function canApplyHiredEquipmentOvertime(
  overtimeEligible: boolean | null | undefined,
  unit: string | null | undefined,
): boolean {
  return overtimeEligible === true && isHoursUnit(unit);
}

export function splitHiredEquipmentOvertimeHours(qty: number): HeOvertimeBuckets {
  if (!Number.isFinite(qty) || qty <= 0) return { straight: 0, ot15: 0, ot20: 0 };
  const straight = Math.min(qty, HE_OT_STRAIGHT_CAP);
  const ot15 = Math.min(Math.max(qty - HE_OT_STRAIGHT_CAP, 0), HE_OT_15_CAP);
  const ot20 = Math.max(qty - HE_OT_STRAIGHT_CAP - HE_OT_15_CAP, 0);
  return { straight, ot15, ot20 };
}

export function overtimeEquivalentHours(qty: number): number {
  const { straight, ot15, ot20 } = splitHiredEquipmentOvertimeHours(qty);
  return straight + ot15 * HE_OT_15_MULTIPLIER + ot20 * HE_OT_20_MULTIPLIER;
}

export function lineAmountWithOvertime(
  qty: number | null,
  rate: number | null,
  overtimeEligible?: boolean | null,
  unit?: string | null,
): number {
  if (qty == null || rate == null || Number.isNaN(qty) || Number.isNaN(rate)) return 0;
  if (!canApplyHiredEquipmentOvertime(overtimeEligible, unit)) return qty * rate;
  return overtimeEquivalentHours(qty) * rate;
}

/**
 * TrueContext additive OT vs WRAP replacement split.
 * When `overtimeEligible` is false, 8–12 hours are omitted once qty > 12 (legacy).
 */
export function hiredEquipmentHourBreakdown(input: {
  overtimeEligible: boolean;
  hoursLabour: number;
  rateBlueBook: number;
  overtime8HourRate: number;
  overtime12HourRate: number;
}): HeHourBreakdown {
  const hours = Number.isFinite(input.hoursLabour) ? input.hoursLabour : 0;
  const rate = Number.isFinite(input.rateBlueBook) ? input.rateBlueBook : 0;
  const ot8 = Number.isFinite(input.overtime8HourRate) ? input.overtime8HourRate : 0;
  const ot12 = Number.isFinite(input.overtime12HourRate) ? input.overtime12HourRate : 0;

  if (input.overtimeEligible) {
    const { straight, ot15, ot20 } = splitHiredEquipmentOvertimeHours(hours);
    return {
      regularQty: straight,
      ot15Qty: ot15,
      ot20Qty: ot20,
      regularCost: straight * rate,
      ot15Cost: ot15 * ot8,
      ot20Cost: ot20 * ot12,
    };
  }

  const ot15Qty = hours > 8 && hours <= 12 ? hours - 8 : 0;
  const ot20Qty = hours > 12 ? hours - 12 : 0;
  return {
    regularQty: hours,
    ot15Qty,
    ot20Qty,
    regularCost: hours * rate,
    ot15Cost: ot15Qty * ot8,
    ot20Cost: ot20Qty * ot12,
  };
}

export function findHe8HourRateValue(
  fields: Array<{ field?: string | null; value?: string | null }>,
): string {
  for (const field of fields) {
    const name = (field.field || '').toLowerCase();
    if (name === '8hourrate' || name === 'overtime8hourrate' || name === 'overtimerate8to12') {
      const valueStr = String(field.value || '').replace(/[^0-9.]/g, '');
      if (parseFloat(valueStr) > 0) return valueStr;
    }
  }
  return '0';
}

export function formatHeOvertimeBreakdownText(input: {
  qty: number | null;
  rate: number | null;
  overtimeEligible?: boolean | null;
  unit?: string | null;
  fuelPerHour?: number | null;
}): string | null {
  const qty = input.qty;
  const rate = input.rate;
  const fuel = input.fuelPerHour != null && input.fuelPerHour > 0 ? input.fuelPerHour : 0;
  const ot = canApplyHiredEquipmentOvertime(input.overtimeEligible, input.unit);
  if (!ot && !(fuel > 0)) return null;
  if (qty == null || rate == null || Number.isNaN(qty) || Number.isNaN(rate)) return null;

  const lines: string[] = [];
  let amount: number;
  if (!ot) {
    const fuelTotal = qty * fuel;
    amount = qty * rate + fuelTotal;
    lines.push(`Rate: ${formatHours(qty)} × ${formatMoney(rate)} = ${formatMoney(qty * rate)}`);
    lines.push(`Fuel: ${formatHours(qty)} × ${formatMoney(fuel)} = ${formatMoney(fuelTotal)}`);
  } else {
    const { straight, ot15, ot20 } = splitHiredEquipmentOvertimeHours(qty);
    if (straight > 0) {
      lines.push(`Regular: ${formatHours(straight)} h × ${formatMoney(rate)} = ${formatMoney(straight * rate)}`);
    }
    if (fuel > 0 && straight > 0) {
      lines.push(`Fuel: ${formatHours(straight)} h × ${formatMoney(fuel)} = ${formatMoney(straight * fuel)}`);
    }
    if (ot15 > 0) {
      const otRate = rate * HE_OT_15_MULTIPLIER;
      lines.push(`OT 1.5×: ${formatHours(ot15)} h × ${formatMoney(otRate)} = ${formatMoney(ot15 * otRate)}`);
    }
    if (ot20 > 0) {
      const dtRate = rate * HE_OT_20_MULTIPLIER;
      lines.push(`OT 2.0×: ${formatHours(ot20)} h × ${formatMoney(dtRate)} = ${formatMoney(ot20 * dtRate)}`);
    }
    if (qty > 0 && qty <= HE_OT_STRAIGHT_CAP) {
      lines.push('All hours at 1× (under 8h).');
    }
    amount = overtimeEquivalentHours(qty) * rate + (fuel > 0 ? straight * fuel : 0);
  }
  lines.push(`Amount: ${formatMoney(amount)}`);
  return lines.join('\n');
}

export function formatHeOvertimeFooterText(
  rows: Array<{
    qty: number | null;
    rate: number | null;
    overtimeEligible?: boolean | null;
    unit?: string | null;
    fuelPerHour?: number | null;
  }>,
): string | null {
  const otRows = rows.filter((row) => canApplyHiredEquipmentOvertime(row.overtimeEligible, row.unit));
  const fuelRows = rows.filter((row) => (row.fuelPerHour ?? 0) > 0);
  if (otRows.length === 0 && fuelRows.length === 0) return null;

  let straightDollars = 0;
  let ot15Dollars = 0;
  let ot20Dollars = 0;
  let fuelDollars = 0;
  let amount = 0;
  for (const row of rows) {
    const fuel = row.fuelPerHour ?? 0;
    const lineAmount = lineAmountWithOvertime(row.qty, row.rate, row.overtimeEligible, row.unit);
    const fuelHours =
      canApplyHiredEquipmentOvertime(row.overtimeEligible, row.unit) && row.qty != null
        ? splitHiredEquipmentOvertimeHours(row.qty).straight
        : (row.qty ?? 0);
    const fuelTotal = fuel > 0 ? fuelHours * fuel : 0;
    const withFuel = lineAmount + fuelTotal;
    amount += withFuel;
    fuelDollars += fuelTotal;
    if (!canApplyHiredEquipmentOvertime(row.overtimeEligible, row.unit)) {
      straightDollars += lineAmount;
      continue;
    }
    if (row.qty == null || row.rate == null) continue;
    const { straight, ot15, ot20 } = splitHiredEquipmentOvertimeHours(row.qty);
    straightDollars += straight * row.rate;
    ot15Dollars += ot15 * row.rate * HE_OT_15_MULTIPLIER;
    ot20Dollars += ot20 * row.rate * HE_OT_20_MULTIPLIER;
  }

  return otRows.length === 0
    ? [`Fuel: ${formatMoney(fuelDollars)}`, `Amount: ${formatMoney(amount)}`].join('\n')
    : [
        `Regular: ${formatMoney(straightDollars)}`,
        ...(fuelDollars > 0 ? [`Fuel: ${formatMoney(fuelDollars)}`] : []),
        `OT 1.5×: ${formatMoney(ot15Dollars)}`,
        `OT 2.0×: ${formatMoney(ot20Dollars)}`,
        `Amount: ${formatMoney(amount)}`,
      ].join('\n');
}
